/**
 * Order Status Badge — renders a merchant-friendly status label
 * with color mapping and tooltip showing the original ERPNext status.
 *
 * Drop-in replacement for <StatusBadge>{rawStatus}</StatusBadge>
 * in Sales Order contexts.
 */

import { StatusBadge } from "@/components/ui/status-badge";
import { useLanguage } from "@/i18n/useLanguage";
import { getOrderStatusDisplay } from "../utils/orderStatusMap";

interface OrderStatusBadgeProps {
  /** Raw ERPNext status value (e.g. "To Deliver and Bill") */
  status: string;
  /** per_delivered float 0-100, for "Partially Shipped" derivation */
  perDelivered?: number;
  /** Marketplace platform status — takes precedence for marketplace orders */
  marketplaceStatus?: string;
  /** When true, "To Deliver and Bill" shows as "Paid · Awaiting Shipment" */
  paymentVerified?: boolean;
  /** custom_refund_status — "Refunded" overrides the displayed status */
  refundStatus?: string;
  className?: string;
}

export function OrderStatusBadge({
  status,
  perDelivered = 0,
  marketplaceStatus,
  paymentVerified,
  refundStatus,
  className,
}: OrderStatusBadgeProps) {
  const { language } = useLanguage();
  const display = getOrderStatusDisplay(status, perDelivered, language, marketplaceStatus, paymentVerified, refundStatus);

  return (
    <StatusBadge color={display.color} className={className}>
      <span title={`ERPNext status: ${display.rawStatus}`}>
        {display.label}
      </span>
    </StatusBadge>
  );
}
